import { Divider, Box, Button, Flex, Grid, Heading, HStack, Spinner, Stack, Text, useBreakpointValue, Skeleton } from "@chakra-ui/react";
import { useEthers } from "@usedapp/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router-dom";
import { AppContent } from "../../components/AppContent";
import { SelectItem } from "../../components/AppSelect";
import { I18N_NAMESPACE } from "../../core/constants/i18n";
import { ICollection } from "../../core/models/collection";
import { INft } from "../../core/models/nft";
import { IUser } from "../../core/models/user";
import { collectionService } from "../../services/collection.service";
import { marketplaceService } from "../../services/marketplace.service";
import { NftItem } from "../Marketplace/components/NftItem";
import { FeaturedNftSlider } from "./components/FeaturedNftSlider";
import { TopCollections } from "./components/TopCollections";
import { TopUsers } from "./components/TopUsers";

const Dashboard = React.memo(() => {
  const { account } = useEthers();
  const navigate = useNavigate();
  const { t } = useTranslation([I18N_NAMESPACE.DASHBOARD]);

  const [collections, setCollections] = useState<ICollection[]>([]);

  const [topCollections, setTopCollections] = useState<ICollection[]>([]);
  const [selectedTopCollectionDateOption, setSelectedTopCollectionDateOption] = useState("1");

  const [nfts, setNfts] = useState<INft[]>([]);
  const [favoriteNfts, setFavoriteNfts] = useState<INft[]>([]);

  const [topUsers, setTopUsers] = useState<IUser[]>([]);
  const [selectedTopUserDateOption, setSelectedTopUserDateOption] = useState("1");
  const [selectedUserOption, setSelectedUserOption] = useState("seller");

  const [selectedCollection, setSelectedCollection] = useState<SelectItem>();
  const [orderBy, setOrderBy] = useState<SelectItem>();

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTopCollection, setIsLoadingTopCollection] = useState(false);
  const [isLoadingTopUser, setIsLoadingTopUser] = useState(false);
  const [isLoadingFavorites, setIsLoadingLoadingFavorites] = useState(false);

  const [page, setPage] = useState(1);
  const [isEndOfList, setIsEndOfList] = useState(false);
  const pageSize = 15;

  const collectionOptions = useMemo(
    () => collections.map((c) => ({ value: c.id, label: c.name || "" })),
    [collections]
  );
  const orderByOptions = useMemo(
    () => [
      { value: "none", label: "None" },
      { value: "asc", label: "Price: Low to High" },
      { value: "desc", label: "Price: High to Low" },
    ],
    []
  );

  const dateOptions = useMemo(
    () => [
      {
        value: "0",
        label: t("1 day"),
      },
      {
        value: "1",
        label: t("7 days"),
      },
      {
        value: "2",
        label: t("30 days"),
      },
    ],
    [t]
  );

  const userOptions = useMemo(
    () => [
      {
        value: "seller",
        label: t("sellers"),
      },
      {
        value: "buyer",
        label: t("buyers"),
      },
    ],
    [t]
  );

  async function getCollections() {
    const response = await collectionService.getCollections();
    setCollections(response?.data || []);
  }

  async function getTopFavoriteNfts() {
    setIsLoadingLoadingFavorites(true);
    const response = await marketplaceService.getTopFavoriteNfts();
    setFavoriteNfts(response?.data || []);
    setIsLoadingLoadingFavorites(false);
  }

  // Get top collections
  useEffect(() => {
    async function getTopCollections() {
      setIsLoadingTopCollection(true);
      const response = await marketplaceService.getTopCollections({ filter_day: +selectedTopCollectionDateOption });
      setTopCollections(response?.data || []);
      setIsLoadingTopCollection(false);
    }

    getTopCollections();
  }, [selectedTopCollectionDateOption]);

  // Get top users
  useEffect(() => {
    async function getTopUsers() {
      setIsLoadingTopUser(true);
      const response = await marketplaceService.getTopUsers({
        filter_day: +selectedTopUserDateOption,
        group_by_buyer: selectedUserOption === "buyer",
      });
      setTopUsers(response?.data || []);
      setIsLoadingTopUser(false);
    }

    getTopUsers();
  }, [selectedTopUserDateOption, selectedUserOption]);

  useEffect(() => {
    getCollections();
    getTopFavoriteNfts();
  }, []);

  useEffect(() => {
    async function getNfts() {
      const collectionId = selectedCollection?.value.toString();
      const response = await marketplaceService.quickSearch({
        name: "",
        sort_direction: orderBy?.value !== "none" ? orderBy?.value?.toString() : undefined,
        sort_field: "price_mantissa",
        collection: collectionId,
        page: page,
        size: pageSize,
        account: account,
      });
      setIsLoading(false);
      setIsEndOfList(!response?.data || (response.data?.length || 0) < pageSize);

      if (page > 1) {
        setNfts((v) => [...v, ...(response.data || [])]);
      } else {
        setNfts(response?.data || []);
      }
    }

    getNfts();
  }, [orderBy, selectedCollection, page, account]);

  const onChangeCollection = useCallback((collection: SelectItem) => {
    setSelectedCollection(collection);
    setPage(1);
    setIsLoading(true);
  }, []);

  const onChangeOrderBy = useCallback((orderBy: SelectItem) => {
    setOrderBy(orderBy);
    setPage(1);
    setIsLoading(true);
  }, []);

  const onClickMint = useCallback(() => {
    navigate("create/start");
  }, [navigate]);

  const onClickExplore = useCallback(() => {
    navigate("marketplace");
  }, [navigate]);

  return (
    <AppContent>
      <Stack spacing={10}>
        <Skeleton isLoaded={!isLoadingFavorites}>
          <Stack direction={{ base: 'column', md: 'row' }}
            spacing={{ base: '10', md: '10' }}>
            <Box width={{ base: "100%", md: "50%" }}>
              <FeaturedNftSlider nfts={favoriteNfts} />
            </Box>

            <Stack spacing={8} alignSelf="center" display={{ base: "none", md: "initial" }}>
              <Heading>{t("Sell Your NFT Now")}</Heading>
              <Text color={"gray.800"} fontSize={"lg"}>
                {t("The World's First Buy Now Pay Later NFT Marketplace!")}
              </Text>
              <HStack spacing={5}>
                <Button w={28} colorScheme={"blue"} onClick={onClickMint}>
                  {t("Create NFT")}
                </Button>
                <Button w={28} colorScheme={"blue"} variant={"outline"} onClick={onClickExplore}>
                  {t("Explore")}
                </Button>
              </HStack>
            </Stack>
          </Stack>
        </Skeleton>

        <TopCollections
          collections={topCollections}
          isLoading={isLoadingTopCollection}
          dateOptions={dateOptions}
          selectedDateOption={selectedTopCollectionDateOption}
          onChangeDateOption={setSelectedTopCollectionDateOption}
        />

        <TopUsers
          users={topUsers}
          isLoading={isLoadingTopUser}
          dateOptions={dateOptions}
          selectedDateOption={selectedTopUserDateOption}
          onChangeDateOption={setSelectedTopUserDateOption}
          userOptions={userOptions}
          selectedUserOption={selectedUserOption}
          onChangeUserOption={setSelectedUserOption}
        />

        <Stack>
          <Text fontSize={"2xl"} fontWeight={700}>
            {t("Explore")}
          </Text>
          <InfiniteScroll
            style={{
              overflow: "hidden",
              padding: "16px 0px",
            }}
            dataLength={nfts.length}
            next={() => {
              setPage((p) => p + 1);
            }}
            hasMore={!isEndOfList}
            loader={
              <Flex alignItems="center" justifyContent="center" marginTop={10}>
                <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
              </Flex>
            }
          >
            <Grid
              templateColumns={{
                base: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
                xl: "repeat(5, 1fr)",
                "2xl": "repeat(6, 1fr)",
              }}
              gap={6}>
              {nfts.map((nft: INft, index: any) => (
                <NftItem nft={nft} account={account} key={`${nft.token_address}-${nft.token_id}-${index}`} />
              ))}
            </Grid>
          </InfiniteScroll>
        </Stack>
      </Stack>
    </AppContent>
  );
});

export default Dashboard;
